import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';

export interface CaptureOptions {
  /**
   * Capture the visible screen rather than the whole page. Use when an
   * overlay is what matters: the image then matches what the user was
   * actually looking at, and stays a sane size.
   */
  viewport?: boolean;
}

export interface ConsentCapture {
  image: string;              // data URL, image/jpeg
  captured_at: string;        // client clock — indicative only, server stamps authoritative time
  terms_version: string;
  privacy_version: string;
  user_agent: string;
  timezone: string;
  viewport: string;
  origin: string;             // frontend domain that served the consent page
  page_url: string;           // full URL incl. query
}

// Bump these whenever the legal copy changes, and archive the matching documents.
export const TERMS_VERSION = 'v1.0';
export const PRIVACY_VERSION = 'v1.0';

const MAX_WIDTH = 2000;
const JPEG_QUALITY = 0.9;
// Render above CSS resolution so text stays crisp; the viewport capture is
// small enough to afford it. Capped because a full-page capture at 2x can
// exceed the browser's canvas limits.
const RENDER_SCALE = Math.min(window.devicePixelRatio || 1, 2);
// Browsers cap canvas dimensions (~16k in Chrome); past that the capture
// silently comes back blank, which would look like a successful record.
const MAX_DIMENSION = 12000;

/**
 * An icon file rewritten to draw in one inherited colour.
 *
 * A CSS mask uses only the file's alpha, so whatever colours are inside it
 * never reach the screen — a masked icon is always a flat silhouette in
 * `currentColor`. Some of these files are shared with an `<img>` use and so
 * carry a baked colour of their own, which is why substituting one literal is
 * not enough: the pencil came out blue because that is what its file says.
 *
 * Pointing every paint at the colour the icon had on screen reproduces what
 * the mask actually drew, whatever the file happens to hold. `none` is left
 * alone — it is the difference between an outline and a blob.
 *
 * The literal rather than `currentColor`: html2canvas serialises an inline
 * SVG and rasterises it on its own, so nothing is left for `currentColor` to
 * inherit from and every icon comes out black.
 */
function silhouette(svg: string, colour: string): string {
  return svg.replace(/(fill|stroke)="(?!none")[^"]*"/gi, `$1="${colour}"`);
}

/** The file behind a `.sico.is-mask`, carried in --ico as url(...). */
function maskIconUrl(node: HTMLElement, style: CSSStyleDeclaration | null | undefined): string | null {
  const raw = node.style.getPropertyValue('--ico')
    || style?.getPropertyValue('--ico')
    || '';
  return /url\(\s*['"]?(.*?)['"]?\s*\)/.exec(raw)?.[1] ?? null;
}

@Injectable({ providedIn: 'root' })
export class ConsentCaptureService {

  /**
   * Captures the agreement screen as it appeared to the user at the moment
   * they accepted. Renders the FULL page — inner scroll areas are flattened in
   * a detached clone so below-the-fold content is included. The live DOM is
   * never touched, so nothing shifts on screen.
   * Returns null on any failure; capture must never block the contract.
   */
  async capture(element: HTMLElement, options: CaptureOptions = {}): Promise<ConsentCapture | null> {
    // Viewport mode photographs the screen as displayed — overlays, dimmed
    // backdrop and all — instead of flattening the page to its full length.
    const viewport = options.viewport === true;

    // Scroll to the top in both modes. The dialog is position:fixed so it does
    // not move, but the page behind it does — and with the page at zero every
    // offset in the capture is zero too. Left scrolled, browsers disagree about
    // how much compensation html2canvas should apply on top of scrollY, which
    // shifted the render relative to where the modal layer is pinned.
    const restore = this.scrollToTop(element);
    // Expand the live DOM before measuring: the clone is only expanded inside
    // onclone, which runs after the canvas has already been sized. Measuring
    // the collapsed page caps the capture at roughly one viewport.
    const collapse = viewport ? () => { } : this.expandLive(element);
    try {
      // The canvas must contain the whole dialog. On iOS the modal is sized in
      // `dvh`, which resolves against a viewport that can be taller than
      // window.innerHeight, so the dialog would otherwise overhang the canvas
      // and have its footer cut off. Take whichever is taller.
      const dialogBox = viewport
        ? (document.querySelector('.legal-modal') as HTMLElement | null)?.getBoundingClientRect()
        : undefined;
      const viewportHeight = dialogBox
        ? Math.max(window.innerHeight, Math.ceil(dialogBox.bottom))
        : window.innerHeight;

      const { width, height } = viewport
        ? { width: document.documentElement.clientWidth, height: viewportHeight }
        : this.fullExtent(element);

      /* Before the canvas, not during it — see loadMaskIcons. An icon fetched
         inside onclone arrives after the pixels have already been drawn. */
      const maskArt = await this.loadMaskIcons(element);

      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: viewport ? RENDER_SCALE : 1,
        logging: false,
        useCORS: true,
        width,
        height,
        windowWidth: width,
        windowHeight: height,
        // Both zero: scrollToTop() above has already put the page at the origin,
        // so there is no offset left to compensate for.
        scrollX: 0,
        scrollY: 0,
        onclone: (doc: Document) => {
          // html2canvas swallows anything thrown in onclone, so one failing
          // hook would silently skip every hook after it. Isolate each.
          const step = (name: string, fn: () => void) => {
            try {
              fn();
            } catch (err) {
              console.error(`[consent] ${name} failed`, err);
            }
          };

          // First, and via CSS rather than DOM surgery: this is the only fix
          // that does not depend on the traversal hooks below running.
          step('suppressNativeControls', () => this.suppressNativeControls(doc));
          step('flattenMaskIcons', () => this.flattenMaskIcons(doc, maskArt));
          step('maskSecrets', () => this.maskSecrets(doc));
          if (!viewport) {
            step('expandScrollAreas', () => this.expandScrollAreas(doc, element));
          }
          step('pinToggleSizes', () => this.pinToggleSizes(element, doc));
          if (viewport) {
            step('pinModalLayer', () => this.pinModalLayer(doc));
          }
        }
      });

      return {
        image: this.downscale(canvas),
        captured_at: new Date().toISOString(),
        terms_version: TERMS_VERSION,
        privacy_version: PRIVACY_VERSION,
        user_agent: navigator.userAgent,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        origin: window.location.origin,
        page_url: window.location.href
      };
    } catch (e) {
      console.error('consent capture failed', e);
      return null;
    } finally {
      collapse();
      restore();
    }
  }

  /**
   * Release viewport-bound heights on the live page so it lays out at full
   * height, and return a callback restoring every inline style touched. The
   * page is put back before the browser paints, so this is not visible.
   */
  private expandLive(root: HTMLElement): () => void {
    const saved: Array<[HTMLElement, string, string, string]> = [];
    const touched = new Set<HTMLElement>();

    const release = (node: HTMLElement) => {
      if (touched.has(node) || node.closest('.side-menu-wrapper')) {
        return;   // already released — re-saving would store the modified style
      }
      if (node.hasAttribute('data-consent-keep-size') ||
          node.closest('[data-consent-keep-size]')) {
        return;   // captured at its on-screen size, scroll position and all
      }
      const clipped = node.scrollHeight > node.clientHeight + 1;
      const viewportSized =
        Math.abs(node.clientHeight - window.innerHeight) < 80 && node.clientHeight > 200;

      if (!clipped && !viewportSized) {
        return;
      }
      touched.add(node);
      saved.push([node, node.style.height, node.style.maxHeight, node.style.overflow]);
      node.style.height = 'auto';
      node.style.maxHeight = 'none';
      node.style.overflow = 'visible';
    };

    // Two passes: expanding a parent can reveal that a child was clipped too,
    // and the first pass reads each child before its parent has grown.
    for (let pass = 0; pass < 2; pass++) {
      release(root);
      root.querySelectorAll<HTMLElement>('*').forEach(release);
    }

    return () => {
      saved.forEach(([node, height, maxHeight, overflow]) => {
        node.style.height = height;
        node.style.maxHeight = maxHeight;
        node.style.overflow = overflow;
      });
    };
  }

  /**
   * Momentarily scroll every container to the top so measurement and render
   * both see the page from its origin. Returns a callback that puts the user
   * back exactly where they were — this happens within one frame, so it is
   * not visible on screen.
   */
  private scrollToTop(root: HTMLElement): () => void {
    const saved: Array<[HTMLElement, number, number]> = [];

    const remember = (node: HTMLElement) => {
      // The dialog is photographed as the user left it — scrolled to wherever
      // they finished reading — so its panes keep their offsets. Only the page
      // behind it is reset.
      if (node.closest('.legal-modal')) {
        return;
      }
      if (node.scrollTop || node.scrollLeft) {
        saved.push([node, node.scrollTop, node.scrollLeft]);
        node.scrollTop = 0;
        node.scrollLeft = 0;
      }
    };

    remember(root);
    root.querySelectorAll<HTMLElement>('*').forEach(remember);

    const pageY = window.scrollY;
    const pageX = window.scrollX;
    if (pageY || pageX) {
      window.scrollTo(0, 0);
    }

    return () => {
      saved.forEach(([node, top, left]) => {
        node.scrollTop = top;
        node.scrollLeft = left;
      });
      if (pageY || pageX) {
        window.scrollTo(pageX, pageY);
      }
    };
  }

  /**
   * html2canvas renders inputs from their value, not the browser's masked
   * display, so a password would otherwise appear in plaintext whenever the
   * user had toggled visibility on. Overwrite in the clone only.
   */
  private maskSecrets(doc: Document): void {
    const sensitive = ['password', 'social_security_number', 'ssn', 'card', 'cvv', 'account_number'];

    doc.querySelectorAll('input').forEach((input: HTMLInputElement) => {
      const name = (input.getAttribute('name') || '').toLowerCase();
      const control = (input.getAttribute('formcontrolname') || '').toLowerCase();

      const isSensitive =
        input.type === 'password' ||
        input.hasAttribute('data-consent-mask') ||
        sensitive.some(s => name.includes(s) || control.includes(s));

      if (isSensitive) {
        input.value = '••••••••';
        input.setAttribute('value', '••••••••');
        input.type = 'text';
      }
    });

    doc.querySelectorAll('[data-consent-exclude]').forEach(el => el.remove());

    this.drawCheckedBoxes(doc);
  }


  /**
   * html2canvas paints form controls from their attributes, not their live
   * state, so a ticked checkbox renders empty — the one detail a consent
   * record most needs to show. Replace each checked box with a drawn tick.
   */
  private drawCheckedBoxes(doc: Document): void {
    doc.querySelectorAll('input[type="checkbox"]').forEach(el => {
      const box = el as HTMLInputElement;
      if (!box.checked) {
        return;
      }
      const mark = doc.createElement('span');
      mark.textContent = '✔';
      mark.setAttribute('style', [
        'display:inline-block',
        'width:16px',
        'height:16px',
        'line-height:16px',
        'text-align:center',
        'font-size:12px',
        'color:#ffffff',
        'background:#139145',
        'border:1px solid #139145',
        'border-radius:3px',
        'margin:2px 8px 0 0',
        'vertical-align:top'
      ].join(';'));
      box.parentNode?.replaceChild(mark, box);
    });
  }





  /**
   * The layout pins itself to the viewport and scrolls content in inner
   * containers. Release those constraints in the clone so the whole page is
   * laid out at full height and nothing is clipped.
   */
  private expandScrollAreas(doc: Document, source?: HTMLElement): void {
    const roots =
      '.login-main-wrapper, .finance-wrapper, .layout-wrapper, .layout-wrapper-inner, .layout-body, .right-content, .right-side-component';
    doc.querySelectorAll(roots).forEach(el => {
      const node = el as HTMLElement;
      node.style.height = 'auto';
      node.style.overflow = 'visible';
    });

    const scrollers =
      '.vh-100, .h-100, .overflow-hidden, .overflow-auto, .overflow-lg-auto, .finance-inner, .finance-body, .hide-scroll-bar';
    doc.querySelectorAll(scrollers).forEach(el => {
      const node = el as HTMLElement;
      node.style.height = 'auto';
      node.style.maxHeight = 'none';
      node.style.minHeight = '0';
      node.style.overflow = 'visible';
    });

    // Class selectors only catch what we thought to list. Sweep the computed
    // style of every element so stylesheet rules like `height: calc(100% -
    // 65px)` or `overflow: hidden` are released too — those clip just as hard
    // and are invisible to a class-name match.
    // Measure against the LIVE element: the clone is not laid out yet, so its
    // computed styles and scrollHeight are unreliable.
    if (!source) {
      return;
    }
    // The clone is a structural copy, so document order pairs each node with
    // its live twin exactly — safer than matching on class names.
    const live = source.querySelectorAll<HTMLElement>('*');
    const copies = doc.querySelectorAll<HTMLElement>('*');
    const pairable = Math.min(live.length, copies.length);

    for (let i = 0; i < pairable; i++) {
      const node = copies[i];
      const twin = live[i];
      if (node.tagName !== twin.tagName || node.closest('.side-menu-wrapper')) {
        continue;   // structures diverged, or the menu is sized deliberately below
      }
      if (node.hasAttribute('data-consent-keep-size') ||
          node.closest('[data-consent-keep-size]')) {
        continue;   // deliberately captured as seen, not expanded
      }
      const computed = window.getComputedStyle(twin);

      const clipsY =
        computed.overflowY === 'auto' || computed.overflowY === 'scroll' ||
        computed.overflowY === 'hidden';

      // Release a height when content is cut off, or when the element is
      // sized to the viewport rather than to its content — `calc(100% - 65px)`
      // does not overflow itself, but it caps everything below it. A plain
      // pixel height is left alone: those are deliberate (signature pad,
      // avatars, icons) and flattening them would distort the layout.
      const clipped = twin.scrollHeight > twin.clientHeight + 1;
      const viewportSized =
        Math.abs(twin.clientHeight - window.innerHeight) < 80 &&
        twin.clientHeight > 200;

      if (clipsY) {
        node.style.overflow = 'visible';
      }
      if (clipped || viewportSized) {
        node.style.height = 'auto';
        node.style.maxHeight = 'none';
        node.style.overflow = 'visible';
      }
    }

    // The clone inherits live scroll offsets. Once overflow is visible those
    // offsets no longer clip, but the content stays shifted up by however far
    // the user had scrolled — so the top of the page renders cut off.
    doc.querySelectorAll<HTMLElement>('*').forEach(node => {
      if (node.closest('[data-consent-keep-size]')) {
        return;   // keep the reader's actual scroll position
      }
      if (node.scrollTop || node.scrollLeft) {
        node.scrollTop = 0;
        node.scrollLeft = 0;
      }
    });
    if (doc.documentElement) {
      doc.documentElement.scrollTop = 0;
    }
    if (doc.body) {
      doc.body.scrollTop = 0;
    }

    // The side menu is a fixed-height column beside a now-taller body. Left on
    // auto it collapses to its content, leaving a white gap down the side.
    const menu = doc.querySelector('.side-menu-wrapper') as HTMLElement | null;
    if (menu) {
      menu.style.height = '100%';
      menu.style.minHeight = '100%';
      menu.style.overflow = 'visible';
    }
  }

  /**
   * Toggle switches are a fixed-size label wrapping an absolutely-positioned
   * slider. html2canvas collapses the label to zero width, so the slider drifts
   * over the text beside it. Pin each one to its real measured size.
   */
  /**
   * The app hides native checkboxes (`input[type=checkbox] { display: none }`)
   * and draws the visible box as a :before on the adjacent label. html2canvas
   * does not honour display:none for form controls, so it paints the native
   * control on top of the styled one — two checkboxes where the page shows
   * one. Drop anything the live page is not actually displaying.
   */
  /**
   * Turns mask-drawn icons back into images for the capture.
   *
   * The panel's icons come in two forms (see the icons READMEs): an `<img>`,
   * and a `<span class="sico is-mask">` whose shape comes from `mask-image`
   * and whose colour comes from `background: currentColor`. html2canvas does
   * not implement CSS masks. It paints the background and ignores the mask —
   * so every masked icon rasterises as a SOLID RECTANGLE of its own colour.
   *
   * That is not cosmetic here. These captures are the record of what a
   * business was looking at when it agreed to something, and coloured blocks
   * where the icons were make a document look tampered with rather than
   * photographed.
   *
   * The clone swaps each one for an `<img>` of the same file at the same size,
   * which html2canvas renders properly. The icon then appears in the colour
   * baked into the file rather than the colour it had on screen — mask files
   * are not authored for a particular colour, so these are usually black.
   * A correctly drawn icon in the wrong shade beats a filled box, and nothing
   * about either is load-bearing in the evidence.
   */
  private flattenMaskIcons(clone: Document, art: ReadonlyMap<string, string>): void {
    // The clone lives in html2canvas's own iframe, so its styles have to be
    // resolved through that document's view rather than this one's.
    const view = clone.defaultView;
    clone.querySelectorAll<HTMLElement>('.sico.is-mask').forEach(node => {
      const style = view?.getComputedStyle(node);
      const url = maskIconUrl(node, style);
      const svg = url && art.get(url);
      if (!svg) { return; }

      /* Size from the computed box: a detached clone may not have been laid
         out, and a zero-sized icon would vanish from the record. */
      const px = (value: string | undefined) =>
        (value && parseFloat(value) > 0 ? value : '16px');

      const slot = clone.createElement('span');
      slot.style.cssText =
        `display:inline-block;width:${px(style?.width)};height:${px(style?.height)};`
        + `vertical-align:middle;opacity:${style?.opacity ?? '1'};`;
      slot.innerHTML = silhouette(svg, style?.color || '#000');
      const drawn = slot.firstElementChild as HTMLElement | null;
      if (drawn) {
        drawn.style.width = '100%';
        drawn.style.height = '100%';
        drawn.style.display = 'block';
      }
      node.replaceWith(slot);
    });
  }

  /**
   * The artwork behind every masked icon in `element`, fetched up front.
   *
   * It has to be in hand before html2canvas starts: an `<img>` created inside
   * `onclone` is not loaded by the time the canvas is rasterised, so it draws
   * as a blank space — which is how the first version of this silently lost
   * every icon it was meant to rescue. Inlining the markup instead means
   * there is nothing left to wait for.
   *
   * Same-origin files out of the app's own assets, so no CORS to negotiate.
   * Cached for the life of the session: the same handful of icons appear on
   * every capture, and they only change with a deploy.
   */
  private async loadMaskIcons(element: HTMLElement): Promise<ReadonlyMap<string, string>> {
    const urls = new Set<string>();
    element.querySelectorAll<HTMLElement>('.sico.is-mask').forEach(node => {
      const url = maskIconUrl(node, getComputedStyle(node));
      if (url && !this.maskArt.has(url)) { urls.add(url); }
    });

    await Promise.all([...urls].map(async url => {
      try {
        const response = await fetch(url);
        const text = await response.text();
        // Only ever an inline <svg>; anything else is a 404 page or a redirect.
        if (response.ok && text.trimStart().startsWith('<svg')) {
          this.maskArt.set(url, text);
        }
      } catch {
        /* One icon that will not load is not worth losing the capture over;
           it simply stays a mask and rasterises as nothing. */
      }
    }));

    return this.maskArt;
  }

  private readonly maskArt = new Map<string, string>();

  /**
   * The app hides native checkboxes with `display: none` and draws the visible
   * box as a :before on the adjacent label. html2canvas does not honour that
   * for form controls — it paints its own native-looking widget, so the
   * capture shows two boxes where the page shows one.
   *
   * `appearance: none` is the fix rather than `display: none`: it tells the
   * renderer not to draw a native widget at all, which html2canvas does
   * respect. Delivered as a stylesheet so it applies during rasterisation
   * regardless of whether the DOM hooks below run.
   */
  private suppressNativeControls(clone: Document): void {
    // Remove the control outright. The stylesheet below is a belt-and-braces
    // fallback: html2canvas takes element geometry from the live DOM, so CSS
    // alone did not reliably stop it painting a native widget. A checkbox
    // followed by a <label> is this app's hide-and-restyle pattern (see
    // styles.scss `input[type=checkbox] { display: none }`), so the visible
    // box is the label's :before and dropping the input loses nothing.
    clone.querySelectorAll<HTMLElement>('input[type="checkbox"], input[type="radio"]')
      .forEach(node => {
        const label = node.nextElementSibling as HTMLElement | null;
        if (label?.tagName === 'LABEL') {
          // The box is painted by the label's :before/:after, which are keyed
          // off `input:checked + label`. Dropping the input takes that selector
          // with it, so stamp the state onto the label first and let the CSS
          // below re-attach the styling to the marker instead.
          if ((node as HTMLInputElement).checked) {
            label.setAttribute('data-consent-checked', '');
          }
          node.remove();
        }
      });

    const css = `
      input[type="checkbox"],
      input[type="radio"] {
        appearance: none !important;
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        width: 0 !important;
        height: 0 !important;
        min-width: 0 !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        outline: 0 !important;
        opacity: 0 !important;
        background: none !important;
      }

      /* Re-attach the checked styling to the marker left behind above, so a
         ticked box still reads as ticked once its input has been removed.
         Mirrors the app's secondary-checkbox.green-bg rules in styles.scss. */
      label[data-consent-checked]:before {
        border: 2px solid rgba(19, 145, 69, 1) !important;
        background: rgba(19, 145, 69, 1) !important;
      }
      label[data-consent-checked]:after {
        content: "" !important;
        width: 11px;
        height: 6px;
        border-bottom: 2px solid #fff !important;
        border-left: 2px solid #fff !important;
        border-bottom-left-radius: 1px;
        position: absolute;
        transform: scale(1) rotate(-45deg);
        left: 5px;
        top: 6px;
        opacity: 1;
      }
    `;

    const style = clone.createElement('style');
    style.setAttribute('data-consent-style', '');
    style.textContent = css;
    (clone.head || clone.documentElement)?.appendChild(style);
  }


  /**
   * Pins the backdrop and the dialog to the coordinates they occupy on screen.
   *
   * Both are `position: fixed`, which html2canvas resolves against the document
   * rather than the viewport — so the backdrop was not painted at all and the
   * dialog could land at the wrong offset. Rewriting them as `absolute` at their
   * measured position makes html2canvas draw the dimmed overlay exactly as the
   * user saw it, with no canvas compositing needed afterwards.
   */
  private pinModalLayer(clone: Document): void {
    const backdrop = document.querySelector('.modal-backdrop');
    const cloneBackdrop = clone.querySelector('.modal-backdrop') as HTMLElement | null;
    if (backdrop && cloneBackdrop) {
      cloneBackdrop.style.position = 'absolute';
      cloneBackdrop.style.top = '0';
      cloneBackdrop.style.left = '0';
      cloneBackdrop.style.width = '100%';
      cloneBackdrop.style.height = '100%';
      cloneBackdrop.style.opacity = '0.5';
      cloneBackdrop.style.backgroundColor = '#000';
    }

    // The dialog's own wrapper is fixed too; place it where it actually sits so
    // it stays on top of the backdrop above.
    const liveDialog = document.querySelector('.modal') as HTMLElement | null;
    const cloneDialog = clone.querySelector('.modal') as HTMLElement | null;
    if (liveDialog && cloneDialog) {
      const rect = liveDialog.getBoundingClientRect();
      cloneDialog.style.position = 'absolute';
      cloneDialog.style.top = `${Math.round(rect.top)}px`;
      cloneDialog.style.left = `${Math.round(rect.left)}px`;
      cloneDialog.style.width = `${Math.round(rect.width)}px`;
      cloneDialog.style.height = `${Math.round(rect.height)}px`;
      cloneDialog.style.overflow = 'visible';
    }
  }

  private pinToggleSizes(source: Document | HTMLElement, clone: Document): void {
    const live = source.querySelectorAll<HTMLElement>('.switch');
    const copies = clone.querySelectorAll<HTMLElement>('.switch');

    copies.forEach((node, i) => {
      const rect = live[i]?.getBoundingClientRect();
      const width = rect?.width || 50;
      const height = rect?.height || 24;

      node.style.width = `${width}px`;
      node.style.height = `${height}px`;
      node.style.flex = `0 0 ${width}px`;
      node.style.display = 'inline-block';
      node.style.position = 'relative';
      node.style.overflow = 'hidden';

      // The slider is absolutely positioned with left/right:0. html2canvas
      // resolves that against the wrong containing block, so it stretches past
      // the switch and over the label beside it. Give it explicit bounds.
      const slider = node.querySelector('.slider') as HTMLElement | null;
      if (slider) {
        slider.style.position = 'absolute';
        slider.style.left = '0';
        slider.style.top = '0';
        slider.style.right = 'auto';
        slider.style.bottom = 'auto';
        slider.style.width = `${width}px`;
        slider.style.height = `${height}px`;
      }
    });
  }

  /**
   * True rendered size of the page. A scrolling child reports its own content
   * height while the parent reports only the visible slice, so walk the tree
   * and take the largest extent relative to the capture root.
   */
  private fullExtent(root: HTMLElement): { width: number; height: number } {
    const base = root.getBoundingClientRect();

    // A keep-size root is captured exactly as it appears, so its own box is
    // the whole extent — measuring children would re-introduce the full
    // scroll length we deliberately chose not to expand.
    if (root.hasAttribute('data-consent-keep-size')) {
      const rect = root.getBoundingClientRect();
      return {
        width: Math.min(Math.ceil(rect.width), MAX_DIMENSION),
        height: Math.min(Math.ceil(rect.height), MAX_DIMENSION)
      };
    }

    let width = Math.max(root.scrollWidth, root.offsetWidth);
    let height = Math.max(root.scrollHeight, root.offsetHeight);

    root.querySelectorAll('*').forEach(el => {
      const node = el as HTMLElement;
      if (!node.getClientRects().length) {
        return;   // hidden / collapsed — contributes nothing
      }
      if (node.closest('[data-consent-keep-size]')) {
        return;   // sized as seen, not by its scroll length
      }
      const rect = node.getBoundingClientRect();

      // Rects are viewport-relative, so anything inside a scrolled container
      // reports a position shifted up by that container's offset. Add the
      // ancestors' scroll back or the page measures short.
      let offsetTop = 0;
      let offsetLeft = 0;
      for (let p = node.parentElement; p && p !== root.parentElement; p = p.parentElement) {
        offsetTop += p.scrollTop;
        offsetLeft += p.scrollLeft;
      }

      width = Math.max(width, rect.left - base.left + offsetLeft + node.scrollWidth);
      height = Math.max(height, rect.top - base.top + offsetTop + node.scrollHeight);
    });

    const result = {
      width: Math.min(Math.ceil(width), MAX_DIMENSION),
      height: Math.min(Math.ceil(height), MAX_DIMENSION)
    };
    return result;
  }

  /** Keep the payload small — a full-page PNG runs to several MB. */
  private downscale(canvas: HTMLCanvasElement): string {
    if (canvas.width <= MAX_WIDTH) {
      return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    }
    const ratio = MAX_WIDTH / canvas.width;
    const scaled = document.createElement('canvas');
    scaled.width = MAX_WIDTH;
    scaled.height = Math.round(canvas.height * ratio);

    const ctx = scaled.getContext('2d');
    if (!ctx) {
      return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, scaled.width, scaled.height);
    ctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
    return scaled.toDataURL('image/jpeg', JPEG_QUALITY);
  }
}
